import React, { createContext, useContext, useEffect, useState } from "react";
import portalApi from "../api/portalClient";
import PortalLicenseGate from "../components/PortalLicenseGate";
import LicenseNotices from "../components/LicenseNotices";

export interface PortalUser {
  id: number;
  customer_id: number;
  customer_name: string;
  email: string;
  full_name?: string;
  role?: string; // "administrator" | "user"
  must_change_password?: boolean;
  totp_enabled?: boolean;
  can_view_invoices?: boolean;
}

export type PortalLoginResult =
  | { ok: true }
  | { ok: false; requires2FA: true; partialToken: string }
  | { ok: false; requires2FA: false; error: string };

interface Ctx {
  user: PortalUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<PortalLoginResult>;
  verifyTwoFactor: (partialToken: string, code: string) => Promise<void>;
  acceptSsoToken: (token: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => void;
}

const PortalAuthContext = createContext<Ctx>(null!);

export function PortalAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("portal_token");
    if (!t) {
      setLoading(false);
      return;
    }
    portalApi
      .get("/api/v1/portal/me")
      .then((r) => setUser(r.data))
      .catch((err) => {
        // Only treat this as a failed session if it's an auth/other error. A 402
        // means the appliance license is locked, not that the token is bad —
        // keep it so the customer is back in once the provider restores service,
        // and let PortalLicenseGate show the unavailable screen.
        if (err?.response?.status !== 402) localStorage.removeItem("portal_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<PortalLoginResult> => {
    try {
      const { data } = await portalApi.post("/api/v1/portal/login", { email, password });
      if (data?.requires_2fa && data?.partial_token) {
        return { ok: false, requires2FA: true, partialToken: data.partial_token };
      }
      localStorage.setItem("portal_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Invalid email or password";
      return { ok: false, requires2FA: false, error: detail };
    }
  };

  const verifyTwoFactor = async (partialToken: string, code: string) => {
    const { data } = await portalApi.post("/api/v1/portal/login/verify-2fa", {
      partial_token: partialToken,
      code,
    });
    localStorage.setItem("portal_token", data.access_token);
    setUser(data.user);
  };

  // Accept a portal token minted by the SSO callback (customer OIDC/SAML) or
  // the relayed AD login, validate it, and establish the session. Mirrors the
  // staff AuthContext.acceptSsoToken.
  const acceptSsoToken = async (token: string) => {
    localStorage.setItem("portal_token", token);
    try {
      const r = await portalApi.get("/api/v1/portal/me");
      setUser(r.data);
    } catch (e) {
      localStorage.removeItem("portal_token");
      throw e;
    }
  };

  const refresh = async () => {
    const { data } = await portalApi.get("/api/v1/portal/me");
    setUser(data);
  };

  const logout = () => {
    localStorage.removeItem("portal_token");
    setUser(null);
  };

  return (
    <PortalAuthContext.Provider
      value={{ user, loading, login, verifyTwoFactor, acceptSsoToken, refresh, logout }}
    >
      {/* License enforcement chrome — present on every portal route (this
          provider wraps each one). The gate swaps in an unavailable screen on a
          hard lock; the notice toast covers grace-period blocked actions. */}
      <PortalLicenseGate>{children}</PortalLicenseGate>
      <LicenseNotices eventName="portal-license:notice" />
    </PortalAuthContext.Provider>
  );
}

export const usePortalAuth = () => useContext(PortalAuthContext);
